import React, { useState, useCallback } from 'react';
import fetchApi from '../utils/api';
import AirDatePicker from '../components/ui/AirDatePicker';
import '../styles/GSTReport.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt = (v) => parseFloat(v || 0).toFixed(2);

const formatDate = (d) => {
    if (!d) return '';
    const parts = d.split('-');
    if (parts.length === 3) {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[parseInt(parts[1], 10) - 1] || parts[1];
        return `${parts[2]}-${month}-${parts[0]}`;
    }
    return d;
};

const formatDateLabel = (d) => {
    if (!d) return '';
    const parts = d.split('-');
    if (parts.length !== 3) return d;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[parseInt(parts[1], 10) - 1] || parts[1];
    return `${parseInt(parts[2], 10)}-${month}-${parts[0]}`;
};

const calcTotal = (data, key) => {
    return (data || []).reduce((sum, row) => sum + (parseFloat(row[key]) || 0), 0);
};


// ─── Empty report structure ───────────────────────────────────────────────────
const EMPTY_REPORT = {
    summary: {
        total_invoices: 0, taxable_value: 0,
        cgst: 0, sgst: 0, igst: 0,
        total_invoice_amount: 0,
        all_sale_invoices: 0, b2b_invoices: 0,
        b2cs_invoices: 0, b2cs_grouped_rows: 0,
        b2cl_invoices: 0, cdnr_count: 0
    },
    all_sale_invoices: [],
    b2b: [],
    b2cs: [],
    b2cl: [],
    cdnr: [],
    cdnur: [],
    exp: [],
    hsn_b2b: [],
    hsn_b2c: [],
    document_summary: []
};

// ─── CSV Export ───────────────────────────────────────────────────────────────
const exportToCSV = (report, startDate, endDate) => {
    const rows = [];
    rows.push(['GSTR-1 Report']);
    rows.push([`Period: ${formatDateLabel(startDate)} to ${formatDateLabel(endDate)}`]);
    rows.push([]);

    rows.push(['SUMMARY']);
    rows.push(['Total Invoices', 'Taxable Value', 'CGST', 'SGST', 'IGST', 'Total Invoice Amount']);
    rows.push([
        report.summary.total_invoices,
        fmt(report.summary.taxable_value),
        fmt(report.summary.cgst),
        fmt(report.summary.sgst),
        fmt(report.summary.igst),
        fmt(report.summary.total_invoice_amount)
    ]);
    rows.push([]);

    rows.push(['ALL SALE INVOICES']);
    rows.push(['GSTR Section', 'GSTIN/UIN', 'Receiver Name', 'Invoice Number', 'Invoice Date',
        'Invoice Value', 'Place of Supply', 'Rate', 'Taxable Value', 'CGST', 'SGST', 'IGST']);
    (report.all_sale_invoices || []).forEach(r => {
        rows.push([r.gstr_section, r.gstin, r.receiver_name, r.invoice_number,
        r.invoice_date, fmt(r.invoice_value), r.place_of_supply, fmt(r.rate),
        fmt(r.taxable_value), fmt(r.cgst), fmt(r.sgst), fmt(r.igst)]);
    });
    rows.push([]);

    rows.push(['B2B']);
    rows.push(['GSTIN/UIN', 'Receiver Name', 'Invoice Number', 'Invoice Date',
        'Invoice Value', 'Place of Supply', 'Rate', 'Taxable Value', 'CGST', 'SGST', 'IGST']);
    (report.b2b || []).forEach(r => {
        rows.push([r.gstin, r.receiver_name, r.invoice_number, r.invoice_date,
        fmt(r.invoice_value), r.place_of_supply, fmt(r.rate), fmt(r.taxable_value),
        fmt(r.cgst), fmt(r.sgst), fmt(r.igst)]);
    });
    rows.push([]);

    rows.push(['B2CL']);
    rows.push(['Invoice Number', 'Invoice Date', 'Invoice Value', 'Place of Supply', 'Rate', 'Taxable Value']);
    (report.b2cl || []).forEach(r => {
        rows.push([r.invoice_number, r.invoice_date, fmt(r.invoice_value), r.place_of_supply, fmt(r.rate), fmt(r.taxable_value)]);
    });
    rows.push([]);

    rows.push(['B2CS (State and Rate Wise Grouped)']);
    rows.push(['Type', 'Place of Supply', 'Rate', 'Taxable Value', 'Cess Amount']);
    (report.b2cs || []).forEach(r => {
        const rateStr = String(r.place_of_supply || '').trim().startsWith('24') ? '9%-9%' : '18%';
        rows.push([r.type, r.place_of_supply, rateStr, fmt(r.taxable_value), fmt(r.cess_amount)]);
    });
    rows.push([]);

    rows.push(['CDNR']);
    rows.push(['GSTIN/UIN of Recipient', 'Receiver Name', 'Note Number', 'Note Date', 'Note Type', 'Place of Supply', 'Note Value', 'Rate', 'Taxable Value']);
    (report.cdnr || []).forEach(r => {
        rows.push([r.gstin, r.receiver_name, r.note_number, r.note_date, r.note_type, r.place_of_supply, fmt(r.note_value), fmt(r.rate), fmt(r.taxable_value)]);
    });
    rows.push([]);

    rows.push(['CDNUR']);
    rows.push(['Note Number', 'Note Date', 'Note Type', 'Place of Supply', 'Note Value', 'Rate', 'Taxable Value']);
    (report.cdnur || []).forEach(r => {
        rows.push([r.note_number, r.note_date, r.note_type, r.place_of_supply, fmt(r.note_value), fmt(r.rate), fmt(r.taxable_value)]);
    });
    rows.push([]);

    rows.push(['EXP']);
    rows.push(['Invoice Number', 'Invoice Date', 'Invoice Value', 'Rate', 'Taxable Value']);
    (report.exp || []).forEach(r => {
        rows.push([r.invoice_number, r.invoice_date, fmt(r.invoice_value), fmt(r.rate), fmt(r.taxable_value)]);
    });
    rows.push([]);

    rows.push(['HSN Summary - B2B']);
    rows.push(['HSN Code', 'Product Description', 'UQC', 'Total Quantity',
        'Taxable Value', 'IGST', 'CGST', 'SGST', 'Total Tax']);
    (report.hsn_b2b || []).forEach(r => {
        rows.push([r.hsn_code, r.product_description, r.uqc, fmt(r.total_quantity),
        fmt(r.taxable_value), fmt(r.igst), fmt(r.cgst), fmt(r.sgst), fmt(r.total_tax)]);
    });
    rows.push([]);

    rows.push(['HSN Summary - B2C']);
    rows.push(['HSN Code', 'Product Description', 'UQC', 'Total Quantity',
        'Taxable Value', 'IGST', 'CGST', 'SGST', 'Total Tax']);
    (report.hsn_b2c || []).forEach(r => {
        rows.push([r.hsn_code, r.product_description, r.uqc, fmt(r.total_quantity),
        fmt(r.taxable_value), fmt(r.igst), fmt(r.cgst), fmt(r.sgst), fmt(r.total_tax)]);
    });
    rows.push([]);

    rows.push(['Document Summary']);
    rows.push(['Sr. No. From', 'Sr. No. To', 'Total Invoices', 'Cancelled']);
    (report.document_summary || []).forEach(r => {
        rows.push([r.sr_no_from, r.sr_no_to, r.total_invoices, r.cancelled]);
    });

    const csvContent = rows.map(r =>
        r.map(c => `"${String(c == null ? '' : c).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `GSTR1_${startDate}_to_${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const SummaryCards = ({ summary }) => (
    <div className="gst-summary-row">
        {[
            { label: 'Total Invoices', value: summary.total_invoices, isCount: true },
            { label: 'Taxable Value', value: fmt(summary.taxable_value) },
            { label: 'CGST', value: fmt(summary.cgst) },
            { label: 'SGST', value: fmt(summary.sgst) },
            { label: 'IGST', value: fmt(summary.igst) },
            { label: 'Total Invoice Amount', value: fmt(summary.total_invoice_amount), highlight: true },
        ].map((card, i) => (
            <div key={i} className={`gst-summary-card${card.highlight ? ' gst-summary-card--highlight' : ''}`}>
                <div className="gst-sc-label">{card.label}</div>
                <div className="gst-sc-value">{card.isCount ? summary.total_invoices : card.value}</div>
            </div>
        ))}
    </div>
);

const CountBadges = ({ summary }) => (
    <div className="gst-count-row">
        <span className="gst-count-badge">All Sale Invoices: <b>{summary.all_sale_invoices}</b></span>
        <span className="gst-count-badge">B2B Invoices: <b>{summary.b2b_invoices}</b></span>
        <span className="gst-count-badge">B2CS Invoices: <b>{summary.b2cs_invoices}</b></span>
        <span className="gst-count-badge">B2CS Grouped Rows: <b>{summary.b2cs_grouped_rows}</b></span>
        <span className="gst-count-badge">B2CL Invoices: <b>{summary.b2cl_invoices}</b></span>
        <span className="gst-count-badge">CDNR/CDNUR/EXP: <b>{summary.cdnr_count}</b></span>
    </div>
);

const TableSection = ({ title, rowCount, children }) => (
    <div className="gst-table-section">
        <div className="gst-table-header">
            <span className="gst-table-title">{title}</span>
            <span className="gst-table-rows">{rowCount} rows</span>
        </div>
        <div className="gst-table-wrap">
            {children}
        </div>
    </div>
);

const EmptyRow = ({ cols, searched }) => (
    <tr>
        <td colSpan={cols} className="gst-empty">
            {searched ? 'No records found for selected period' : 'Search to load data'}
        </td>
    </tr>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function GSTReport() {
    const today = new Date();
    const firstDay = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const lastDayStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;

    const [startDate, setStartDate] = useState(firstDay);
    const [endDate, setEndDate] = useState(lastDayStr);
    const [report, setReport] = useState(EMPTY_REPORT);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);

    const handleSearch = useCallback(async () => {
        if (!startDate || !endDate) {
            setError('Please select both start and end dates.');
            return;
        }
        if (startDate > endDate) {
            setError('Start date cannot be after end date.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await fetchApi(`/gst-report?startDate=${startDate}&endDate=${endDate}`);
            setReport(data);
            setSearched(true);
        } catch (err) {
            console.error('[GSTReport] fetch error:', err);
            setError(err?.message || 'Failed to load GST report. Please try again.');
            // Reset to empty but still show tables
            setReport(EMPTY_REPORT);
            setSearched(true);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    const handleExport = () => {
        if (!searched) return;
        exportToCSV(report, startDate, endDate);
    };

    return (
        <div className="gst-report-page">
            <div className="gst-report-inner">
                <h1 className="gst-report-title">GSTR-1 Report</h1>

                {/* ── Filter Card ── */}
                <div className="gst-filter-card">
                    <div className="gst-filter-label">Report Filter</div>
                    <div className="gst-filter-row">
                        <div className="gst-filter-field">
                            <label className="gst-field-label" htmlFor="gst-start-date">Start Date</label>
                            <AirDatePicker
                                id="gst-start-date"
                                className="gst-date-input"
                                value={startDate}
                                onChange={val => setStartDate(val)}
                                placeholder="Start Date"
                            />
                        </div>
                        <div className="gst-filter-field">
                            <label className="gst-field-label" htmlFor="gst-end-date">End Date</label>
                            <AirDatePicker
                                id="gst-end-date"
                                className="gst-date-input"
                                value={endDate}
                                onChange={val => setEndDate(val)}
                                placeholder="End Date"
                            />
                        </div>
                        <div className="gst-filter-actions">
                            <button
                                id="gst-search-btn"
                                className="gst-btn gst-btn--primary"
                                onClick={handleSearch}
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="gst-btn-spinner" />
                                ) : (
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <circle cx="11" cy="11" r="8" />
                                        <path d="M21 21l-4.35-4.35" />
                                    </svg>
                                )}
                                {loading ? 'Searching...' : 'Filter'}
                            </button>
                            <button
                                id="gst-export-btn"
                                className="gst-btn gst-btn--success"
                                onClick={handleExport}
                                disabled={!searched || loading}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="7 10 12 15 17 10" />
                                    <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Export Excel
                            </button>
                        </div>
                    </div>

                    {searched && (
                        <div className="gst-period-label">
                            <b>Report Period:</b> {formatDateLabel(startDate)} to {formatDateLabel(endDate)}&nbsp;
                            <span className="gst-period-note">(Invoice Date)</span>
                        </div>
                    )}
                </div>

                {/* ── Error ── */}
                {error && (
                    <div className="gst-error-msg">
                        ⚠️ {error}
                    </div>
                )}

                {/* ── Summary Cards (always visible) ── */}
                <SummaryCards summary={report.summary} />

                {/* ── Count Badges (always visible) ── */}
                <CountBadges summary={report.summary} />

                {/* ── All Sale Invoices ── */}
                <TableSection title="All Sale Invoices" rowCount={report.all_sale_invoices.length}>
                    <table className="gst-table">
                        <thead>
                            <tr>
                                <th>GSTR Section</th>
                                <th>GSTIN/UIN</th>
                                <th>Receiver Name</th>
                                <th>Invoice Number</th>
                                <th>Invoice Date</th>
                                <th>Invoice Value</th>
                                <th>Place of Supply</th>
                                <th>Rate</th>
                                <th>Taxable Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.all_sale_invoices.length === 0
                                ? <EmptyRow cols={9} searched={searched} />
                                : (
                                    <>
                                        {report.all_sale_invoices.map((row, i) => (
                                            <tr key={i}>
                                                <td>
                                                    <span className={`gst-badge gst-badge--${row.gstr_section.toLowerCase()}`}>
                                                        {row.gstr_section}
                                                    </span>
                                                </td>
                                                <td className="gst-mono">{row.gstin}</td>
                                                <td>{row.receiver_name}</td>
                                                <td className="gst-mono">{row.invoice_number}</td>
                                                <td>{formatDate(row.invoice_date)}</td>
                                                <td className="gst-amount">{fmt(row.invoice_value)}</td>
                                                <td>{row.place_of_supply}</td>
                                                <td className="gst-num">{fmt(row.rate)}</td>
                                                <td className="gst-amount">{fmt(row.taxable_value)}</td>
                                            </tr>
                                        ))}
                                        <tr className="gst-table-total-row">
                                            <td colSpan={5} className="gst-table-total-label">TOTAL</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.all_sale_invoices, 'invoice_value'))}</td>
                                            <td></td>
                                            <td></td>
                                            <td className="gst-amount">{fmt(calcTotal(report.all_sale_invoices, 'taxable_value'))}</td>
                                        </tr>
                                    </>
                                )
                            }
                        </tbody>
                    </table>
                </TableSection>

                {/* ── B2B Table ── */}
                <TableSection title="B2B" rowCount={report.b2b.length}>
                    <table className="gst-table">
                        <thead>
                            <tr>
                                <th>GSTIN/UIN of Recipient</th>
                                <th>Receiver Name</th>
                                <th>Invoice Number</th>
                                <th>Invoice Date</th>
                                <th>Invoice Value</th>
                                <th>Place of Supply</th>
                                <th>Rate</th>
                                <th>Taxable Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.b2b.length === 0
                                ? <EmptyRow cols={8} searched={searched} />
                                : (
                                    <>
                                        {report.b2b.map((row, i) => (
                                            <tr key={i}>
                                                <td className="gst-mono">{row.gstin}</td>
                                                <td>{row.receiver_name}</td>
                                                <td className="gst-mono">{row.invoice_number}</td>
                                                <td>{formatDate(row.invoice_date)}</td>
                                                <td className="gst-amount">{fmt(row.invoice_value)}</td>
                                                <td>{row.place_of_supply}</td>
                                                <td className="gst-num">{fmt(row.rate)}</td>
                                                <td className="gst-amount">{fmt(row.taxable_value)}</td>
                                            </tr>
                                        ))}
                                        <tr className="gst-table-total-row">
                                            <td colSpan={4} className="gst-table-total-label">TOTAL</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.b2b, 'invoice_value'))}</td>
                                            <td></td>
                                            <td></td>
                                            <td className="gst-amount">{fmt(calcTotal(report.b2b, 'taxable_value'))}</td>
                                        </tr>
                                    </>
                                )
                            }
                        </tbody>
                    </table>
                </TableSection>

                {/* ── B2CL Table ── */}
                <TableSection title="B2CL" rowCount={report.b2cl?.length || 0}>
                    <table className="gst-table">
                        <thead>
                            <tr>
                                <th>Invoice Number</th>
                                <th>Invoice Date</th>
                                <th>Invoice Value</th>
                                <th>Place of Supply</th>
                                <th>Rate</th>
                                <th>Taxable Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!report.b2cl || report.b2cl.length === 0
                                ? <EmptyRow cols={6} searched={searched} />
                                : (
                                    <>
                                        {report.b2cl.map((row, i) => (
                                            <tr key={i}>
                                                <td className="gst-mono">{row.invoice_number}</td>
                                                <td>{formatDate(row.invoice_date)}</td>
                                                <td className="gst-amount">{fmt(row.invoice_value)}</td>
                                                <td>{row.place_of_supply}</td>
                                                <td className="gst-num">{fmt(row.rate)}</td>
                                                <td className="gst-amount">{fmt(row.taxable_value)}</td>
                                            </tr>
                                        ))}
                                        <tr className="gst-table-total-row">
                                            <td colSpan={2} className="gst-table-total-label">TOTAL</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.b2cl, 'invoice_value'))}</td>
                                            <td></td>
                                            <td></td>
                                            <td className="gst-amount">{fmt(calcTotal(report.b2cl, 'taxable_value'))}</td>
                                        </tr>
                                    </>
                                )
                            }
                        </tbody>
                    </table>
                </TableSection>

                {/* ── B2CS Table ── */}
                <TableSection title="B2CS (State and Rate Wise Grouped)" rowCount={report.b2cs.length}>
                    <table className="gst-table">
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Place of Supply</th>
                                <th>Rate</th>
                                <th>Taxable Value</th>
                                <th>Cess Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.b2cs.length === 0
                                ? <EmptyRow cols={5} searched={searched} />
                                : (
                                    <>
                                        {report.b2cs.map((row, i) => (
                                            <tr key={i}>
                                                <td>{row.type}</td>
                                                <td>{row.place_of_supply}</td>
                                                <td className="gst-num">
                                                    {String(row.place_of_supply || '').trim().startsWith('24') ? '9%-9%' : '18%'}
                                                </td>
                                                <td className="gst-amount">{fmt(row.taxable_value)}</td>
                                                <td className="gst-amount">{fmt(row.cess_amount)}</td>
                                            </tr>
                                        ))}
                                        <tr className="gst-table-total-row">
                                            <td colSpan={3} className="gst-table-total-label">TOTAL</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.b2cs, 'taxable_value'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.b2cs, 'cess_amount'))}</td>
                                        </tr>
                                    </>
                                )
                            }
                        </tbody>
                    </table>
                </TableSection>

                {/* ── CDNR Table ── */}
                <TableSection title="CDNR" rowCount={report.cdnr?.length || 0}>
                    <table className="gst-table">
                        <thead>
                            <tr>
                                <th>GSTIN/UIN of Recipient</th>
                                <th>Receiver Name</th>
                                <th>Note Number</th>
                                <th>Note Date</th>
                                <th>Note Type</th>
                                <th>Place of Supply</th>
                                <th>Note Value</th>
                                <th>Rate</th>
                                <th>Taxable Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!report.cdnr || report.cdnr.length === 0
                                ? <EmptyRow cols={9} searched={searched} />
                                : (
                                    <>
                                        {report.cdnr.map((row, i) => (
                                            <tr key={i}>
                                                <td className="gst-mono">{row.gstin}</td>
                                                <td>{row.receiver_name}</td>
                                                <td className="gst-mono">{row.note_number}</td>
                                                <td>{formatDate(row.note_date)}</td>
                                                <td>{row.note_type}</td>
                                                <td>{row.place_of_supply}</td>
                                                <td className="gst-amount">{fmt(row.note_value)}</td>
                                                <td className="gst-num">{fmt(row.rate)}</td>
                                                <td className="gst-amount">{fmt(row.taxable_value)}</td>
                                            </tr>
                                        ))}
                                        <tr className="gst-table-total-row">
                                            <td colSpan={6} className="gst-table-total-label">TOTAL</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.cdnr, 'note_value'))}</td>
                                            <td></td>
                                            <td className="gst-amount">{fmt(calcTotal(report.cdnr, 'taxable_value'))}</td>
                                        </tr>
                                    </>
                                )
                            }
                        </tbody>
                    </table>
                </TableSection>

                {/* ── CDNUR Table ── */}
                <TableSection title="CDNUR" rowCount={report.cdnur?.length || 0}>
                    <table className="gst-table">
                        <thead>
                            <tr>
                                <th>Note Number</th>
                                <th>Note Date</th>
                                <th>Note Type</th>
                                <th>Place of Supply</th>
                                <th>Note Value</th>
                                <th>Rate</th>
                                <th>Taxable Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!report.cdnur || report.cdnur.length === 0
                                ? <EmptyRow cols={7} searched={searched} />
                                : (
                                    <>
                                        {report.cdnur.map((row, i) => (
                                            <tr key={i}>
                                                <td className="gst-mono">{row.note_number}</td>
                                                <td>{formatDate(row.note_date)}</td>
                                                <td>{row.note_type}</td>
                                                <td>{row.place_of_supply}</td>
                                                <td className="gst-amount">{fmt(row.note_value)}</td>
                                                <td className="gst-num">{fmt(row.rate)}</td>
                                                <td className="gst-amount">{fmt(row.taxable_value)}</td>
                                            </tr>
                                        ))}
                                        <tr className="gst-table-total-row">
                                            <td colSpan={4} className="gst-table-total-label">TOTAL</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.cdnur, 'note_value'))}</td>
                                            <td></td>
                                            <td className="gst-amount">{fmt(calcTotal(report.cdnur, 'taxable_value'))}</td>
                                        </tr>
                                    </>
                                )
                            }
                        </tbody>
                    </table>
                </TableSection>

                {/* ── EXP Table ── */}
                <TableSection title="EXP" rowCount={report.exp?.length || 0}>
                    <table className="gst-table">
                        <thead>
                            <tr>
                                <th>Invoice Number</th>
                                <th>Invoice Date</th>
                                <th>Invoice Value</th>
                                <th>Rate</th>
                                <th>Taxable Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {!report.exp || report.exp.length === 0
                                ? <EmptyRow cols={5} searched={searched} />
                                : (
                                    <>
                                        {report.exp.map((row, i) => (
                                            <tr key={i}>
                                                <td className="gst-mono">{row.invoice_number}</td>
                                                <td>{formatDate(row.invoice_date)}</td>
                                                <td className="gst-amount">{fmt(row.invoice_value)}</td>
                                                <td className="gst-num">{fmt(row.rate)}</td>
                                                <td className="gst-amount">{fmt(row.taxable_value)}</td>
                                            </tr>
                                        ))}
                                        <tr className="gst-table-total-row">
                                            <td colSpan={2} className="gst-table-total-label">TOTAL</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.exp, 'invoice_value'))}</td>
                                            <td></td>
                                            <td className="gst-amount">{fmt(calcTotal(report.exp, 'taxable_value'))}</td>
                                        </tr>
                                    </>
                                )
                            }
                        </tbody>
                    </table>
                </TableSection>

                {/* ── HSN Summary B2B ── */}
                <TableSection title="HSN Summary - B2B" rowCount={report.hsn_b2b.length}>
                    <table className="gst-table">
                        <thead>
                            <tr>
                                <th>HSN Code</th>
                                <th>Product Description</th>
                                <th>UQC</th>
                                <th>Total Quantity</th>
                                <th>Taxable Value</th>
                                <th>IGST</th>
                                <th>CGST</th>
                                <th>SGST</th>
                                <th>Total Tax</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.hsn_b2b.length === 0
                                ? <EmptyRow cols={9} searched={searched} />
                                : (
                                    <>
                                        {report.hsn_b2b.map((row, i) => (
                                            <tr key={i}>
                                                <td className="gst-mono">{row.hsn_code}</td>
                                                <td>{row.product_description}</td>
                                                <td>{row.uqc}</td>
                                                <td className="gst-num">{fmt(row.total_quantity)}</td>
                                                <td className="gst-amount">{fmt(row.taxable_value)}</td>
                                                <td className="gst-amount">{fmt(row.igst)}</td>
                                                <td className="gst-amount">{fmt(row.cgst)}</td>
                                                <td className="gst-amount">{fmt(row.sgst)}</td>
                                                <td className="gst-amount">{fmt(row.total_tax)}</td>
                                            </tr>
                                        ))}
                                        <tr className="gst-table-total-row">
                                            <td colSpan={3} className="gst-table-total-label">TOTAL</td>
                                            <td className="gst-num">{fmt(calcTotal(report.hsn_b2b, 'total_quantity'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.hsn_b2b, 'taxable_value'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.hsn_b2b, 'igst'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.hsn_b2b, 'cgst'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.hsn_b2b, 'sgst'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.hsn_b2b, 'total_tax'))}</td>
                                        </tr>
                                    </>
                                )
                            }
                        </tbody>
                    </table>
                </TableSection>

                {/* ── HSN Summary B2C ── */}
                <TableSection title="HSN Summary - B2C" rowCount={report.hsn_b2c.length}>
                    <table className="gst-table">
                        <thead>
                            <tr>
                                <th>HSN Code</th>
                                <th>Product Description</th>
                                <th>UQC</th>
                                <th>Total Quantity</th>
                                <th>Taxable Value</th>
                                <th>IGST</th>
                                <th>CGST</th>
                                <th>SGST</th>
                                <th>Total Tax</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.hsn_b2c.length === 0
                                ? <EmptyRow cols={9} searched={searched} />
                                : (
                                    <>
                                        {report.hsn_b2c.map((row, i) => (
                                            <tr key={i}>
                                                <td className="gst-mono">{row.hsn_code}</td>
                                                <td>{row.product_description}</td>
                                                <td>{row.uqc}</td>
                                                <td className="gst-num">{fmt(row.total_quantity)}</td>
                                                <td className="gst-amount">{fmt(row.taxable_value)}</td>
                                                <td className="gst-amount">{fmt(row.igst)}</td>
                                                <td className="gst-amount">{fmt(row.cgst)}</td>
                                                <td className="gst-amount">{fmt(row.sgst)}</td>
                                                <td className="gst-amount">{fmt(row.total_tax)}</td>
                                            </tr>
                                        ))}
                                        <tr className="gst-table-total-row">
                                            <td colSpan={3} className="gst-table-total-label">TOTAL</td>
                                            <td className="gst-num">{fmt(calcTotal(report.hsn_b2c, 'total_quantity'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.hsn_b2c, 'taxable_value'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.hsn_b2c, 'igst'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.hsn_b2c, 'cgst'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.hsn_b2c, 'sgst'))}</td>
                                            <td className="gst-amount">{fmt(calcTotal(report.hsn_b2c, 'total_tax'))}</td>
                                        </tr>
                                    </>
                                )
                            }
                        </tbody>
                    </table>
                </TableSection>

                {/* ── Document Summary ── */}
                <TableSection title="Document Summary" rowCount={report.document_summary.length}>
                    <table className="gst-table">
                        <thead>
                            <tr>
                                <th>Sr. No. From</th>
                                <th>Sr. No. To</th>
                                <th>Total Invoice Number</th>
                                <th>Cancelled</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.document_summary.length === 0
                                ? <EmptyRow cols={4} searched={searched} />
                                : (
                                    <>
                                        {report.document_summary.map((row, i) => (
                                            <tr key={i}>
                                                <td className="gst-mono">{row.sr_no_from}</td>
                                                <td className="gst-mono">{row.sr_no_to}</td>
                                                <td className="gst-num">{row.total_invoices}</td>
                                                <td className="gst-num">{row.cancelled}</td>
                                            </tr>
                                        ))}
                                        <tr className="gst-table-total-row">
                                            <td colSpan={2} className="gst-table-total-label">TOTAL</td>
                                            <td className="gst-num">{calcTotal(report.document_summary, 'total_invoices')}</td>
                                            <td className="gst-num">{calcTotal(report.document_summary, 'cancelled')}</td>
                                        </tr>
                                    </>
                                )
                            }
                        </tbody>
                    </table>
                </TableSection>
            </div>
        </div>
    );
}
